import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Stack
} from '@mui/material';
import { 
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Delete as DeleteIcon,
  Code as CodeIcon,
  ViewList as ViewListIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { progressStorageService } from '../../services/progressStorageService';
import { fetchAdvisorConfig } from '../../services/advisorConfigService';
import { useNavigate } from 'react-router-dom';

interface IncompleteForm {
  sessionId: string;
  customerId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  currentStep: number;
  stepName: string;
  lastUpdated: string;
  formData: any;
}

interface FieldStatus {
  name: string;
  value: any;
  isFilled: boolean;
  displayValue?: string;
}

interface SectionStatus {
  name: string;
  fields: FieldStatus[];
  completionPercentage: number;
}

const IncompleteFormsView: React.FC = () => {
  const navigate = useNavigate();
  const [forms, setForms] = useState<IncompleteForm[]>([]);
  const [filteredForms, setFilteredForms] = useState<IncompleteForm[]>([]);
  const [selectedForm, setSelectedForm] = useState<IncompleteForm | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'json' | 'fields'>('fields');
  const [advisorNames, setAdvisorNames] = useState<Map<string, string>>(new Map());

  // Helper function to check if a value is filled
  const isFieldFilled = (value: any): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim() !== '';
    if (typeof value === 'number') return value > 0;  // Only consider positive numbers as filled
    if (typeof value === 'boolean') return true;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return false;
  };


  // Analyze form data and return section status - matching actual form structure from emailService
  const analyzeFormData = (formData: any): SectionStatus[] => {
    const sections: SectionStatus[] = [];

    // Personal Information Section
    const personalFields: FieldStatus[] = [];
    const personalInfo = formData?.personalInfo || {};
    const contactInfo = formData?.contactInfo || {};
    
    personalFields.push({
      name: 'First Name',
      value: personalInfo.client1FirstName,
      isFilled: isFieldFilled(personalInfo.client1FirstName),
      displayValue: personalInfo.client1FirstName || ''
    });
    
    personalFields.push({
      name: 'Last Name',
      value: personalInfo.client1LastName,
      isFilled: isFieldFilled(personalInfo.client1LastName),
      displayValue: personalInfo.client1LastName || ''
    });
    
    personalFields.push({
      name: 'Email',
      value: contactInfo.email || personalInfo.email,
      isFilled: isFieldFilled(contactInfo.email || personalInfo.email),
      displayValue: contactInfo.email || personalInfo.email || ''
    });
    
    personalFields.push({
      name: 'Phone',
      value: contactInfo.phone || personalInfo.phone,
      isFilled: isFieldFilled(contactInfo.phone || personalInfo.phone),
      displayValue: contactInfo.phone || personalInfo.phone || ''
    });
    
    personalFields.push({
      name: 'Date of Birth',
      value: personalInfo.client1DateOfBirth,
      isFilled: isFieldFilled(personalInfo.client1DateOfBirth),
      displayValue: personalInfo.client1DateOfBirth || ''
    });
    
    personalFields.push({
      name: 'Has Partner',
      value: personalInfo.hasPartner,
      isFilled: personalInfo.hasPartner !== undefined,
      displayValue: personalInfo.hasPartner ? 'Yes' : 'No'
    });
    
    if (personalInfo.hasPartner) {
      personalFields.push({
        name: 'Partner First Name',
        value: personalInfo.client2FirstName,
        isFilled: isFieldFilled(personalInfo.client2FirstName),
        displayValue: personalInfo.client2FirstName || ''
      });
      
      personalFields.push({
        name: 'Partner Last Name',
        value: personalInfo.client2LastName,
        isFilled: isFieldFilled(personalInfo.client2LastName),
        displayValue: personalInfo.client2LastName || ''
      });
      
      personalFields.push({
        name: 'Partner Date of Birth',
        value: personalInfo.client2DateOfBirth,
        isFilled: isFieldFilled(personalInfo.client2DateOfBirth),
        displayValue: personalInfo.client2DateOfBirth || ''
      });
    }
    
    const personalFilledCount = personalFields.filter(f => f.isFilled).length;
    sections.push({
      name: 'Personal Information',
      fields: personalFields,
      completionPercentage: Math.round((personalFilledCount / personalFields.length) * 100)
    });

    // Employment & Income Section
    const employmentFields: FieldStatus[] = [];
    const employmentInfo = formData?.employmentInfo || {};
    
    employmentFields.push({
      name: 'Is Retired',
      value: employmentInfo.isRetired,
      isFilled: employmentInfo.isRetired !== undefined,
      displayValue: employmentInfo.isRetired ? 'Yes' : 'No'
    });
    
    employmentFields.push({
      name: 'Current Income',
      value: employmentInfo.currentIncome,
      isFilled: isFieldFilled(employmentInfo.currentIncome),
      displayValue: employmentInfo.currentIncome ? `$${employmentInfo.currentIncome.toLocaleString()}` : ''
    });
    
    employmentFields.push({
      name: 'Expected Retirement Age',
      value: employmentInfo.expectedRetirementAge,
      isFilled: isFieldFilled(employmentInfo.expectedRetirementAge),
      displayValue: employmentInfo.expectedRetirementAge ? `${employmentInfo.expectedRetirementAge} years` : ''
    });
    
    // Partner Employment Information
    const partnerEmployment = employmentInfo.partnerInfo || {};
    if (personalInfo.hasPartner) {
      employmentFields.push({
        name: 'Partner Is Retired',
        value: partnerEmployment.isRetired,
        isFilled: partnerEmployment.isRetired !== undefined,
        displayValue: partnerEmployment.isRetired ? 'Yes' : 'No'
      });
      
      employmentFields.push({
        name: 'Partner Income',
        value: partnerEmployment.currentIncome,
        isFilled: isFieldFilled(partnerEmployment.currentIncome),
        displayValue: partnerEmployment.currentIncome ? `$${partnerEmployment.currentIncome.toLocaleString()}` : ''
      });
      
      employmentFields.push({
        name: 'Partner Expected Retirement Age',
        value: partnerEmployment.expectedRetirementAge,
        isFilled: isFieldFilled(partnerEmployment.expectedRetirementAge),
        displayValue: partnerEmployment.expectedRetirementAge ? `${partnerEmployment.expectedRetirementAge} years` : ''
      });
    }
    
    const employmentFilledCount = employmentFields.filter(f => f.isFilled).length;
    sections.push({
      name: 'Employment & Income',
      fields: employmentFields,
      completionPercentage: Math.round((employmentFilledCount / employmentFields.length) * 100)
    });

    // Social Security & Pensions Section
    const socialSecurityFields: FieldStatus[] = [];
    const socialSecurityInfo = formData?.socialSecurityInfo || {};
    
    // Client Social Security
    socialSecurityFields.push({
      name: 'Is Claiming Social Security',
      value: socialSecurityInfo.isClaiming,
      isFilled: socialSecurityInfo.isClaiming !== undefined,
      displayValue: socialSecurityInfo.isClaiming ? 'Yes' : 'No'
    });
    
    if (socialSecurityInfo.client1SSCurrentAmount !== undefined) {
      socialSecurityFields.push({
        name: 'Current Annual Benefit',
        value: socialSecurityInfo.client1SSCurrentAmount,
        isFilled: isFieldFilled(socialSecurityInfo.client1SSCurrentAmount),
        displayValue: socialSecurityInfo.client1SSCurrentAmount ? `$${socialSecurityInfo.client1SSCurrentAmount.toLocaleString()}` : ''
      });
    }
    
    if (socialSecurityInfo.client1SSProjectedAmount !== undefined) {
      socialSecurityFields.push({
        name: 'Projected Annual Benefit',
        value: socialSecurityInfo.client1SSProjectedAmount,
        isFilled: isFieldFilled(socialSecurityInfo.client1SSProjectedAmount),
        displayValue: socialSecurityInfo.client1SSProjectedAmount ? `$${socialSecurityInfo.client1SSProjectedAmount.toLocaleString()}` : ''
      });
    }
    
    if (socialSecurityInfo.client1SSClaimingAge !== undefined) {
      socialSecurityFields.push({
        name: 'SS Claiming Age',
        value: socialSecurityInfo.client1SSClaimingAge,
        isFilled: isFieldFilled(socialSecurityInfo.client1SSClaimingAge),
        displayValue: socialSecurityInfo.client1SSClaimingAge ? `${socialSecurityInfo.client1SSClaimingAge} years` : ''
      });
    }
    
    // Partner Social Security
    if (personalInfo.hasPartner) {
      if (socialSecurityInfo.partnerIsClaiming !== undefined) {
        socialSecurityFields.push({
          name: 'Partner Is Claiming SS',
          value: socialSecurityInfo.partnerIsClaiming,
          isFilled: socialSecurityInfo.partnerIsClaiming !== undefined,
          displayValue: socialSecurityInfo.partnerIsClaiming ? 'Yes' : 'No'
        });
      }
      
      if (socialSecurityInfo.client2SSCurrentAmount !== undefined) {
        socialSecurityFields.push({
          name: "Partner's Current Annual Benefit",
          value: socialSecurityInfo.client2SSCurrentAmount,
          isFilled: isFieldFilled(socialSecurityInfo.client2SSCurrentAmount),
          displayValue: socialSecurityInfo.client2SSCurrentAmount ? `$${socialSecurityInfo.client2SSCurrentAmount.toLocaleString()}` : ''
        });
      }
      
      if (socialSecurityInfo.client2SSProjectedAmount !== undefined) {
        socialSecurityFields.push({
          name: "Partner's Projected Annual Benefit",
          value: socialSecurityInfo.client2SSProjectedAmount,
          isFilled: isFieldFilled(socialSecurityInfo.client2SSProjectedAmount),
          displayValue: socialSecurityInfo.client2SSProjectedAmount ? `$${socialSecurityInfo.client2SSProjectedAmount.toLocaleString()}` : ''
        });
      }
      
      if (socialSecurityInfo.client2SSClaimingAge !== undefined) {
        socialSecurityFields.push({
          name: 'Partner SS Claiming Age',
          value: socialSecurityInfo.client2SSClaimingAge,
          isFilled: isFieldFilled(socialSecurityInfo.client2SSClaimingAge),
          displayValue: socialSecurityInfo.client2SSClaimingAge ? `${socialSecurityInfo.client2SSClaimingAge} years` : ''
        });
      }
    }
    
    // Pensions are stored in employmentInfo, not a separate pensionInfo object
    if (employmentInfo.hasPension !== undefined) {
      socialSecurityFields.push({
        name: 'Has Pension',
        value: employmentInfo.hasPension,
        isFilled: employmentInfo.hasPension !== undefined,
        displayValue: employmentInfo.hasPension ? 'Yes' : 'No'
      });
      
      if (employmentInfo.hasPension || employmentInfo.pensionStartAge || employmentInfo.pensionBenefitAmount) {
        if (employmentInfo.pensionStartAge !== undefined) {
          socialSecurityFields.push({
            name: 'Pension Start Age',
            value: employmentInfo.pensionStartAge,
            isFilled: isFieldFilled(employmentInfo.pensionStartAge),
            displayValue: employmentInfo.pensionStartAge ? `${employmentInfo.pensionStartAge} years` : ''
          });
        }
        
        if (employmentInfo.pensionBenefitAmount !== undefined) {
          socialSecurityFields.push({
            name: 'Pension Benefit Amount',
            value: employmentInfo.pensionBenefitAmount,
            isFilled: isFieldFilled(employmentInfo.pensionBenefitAmount),
            displayValue: employmentInfo.pensionBenefitAmount ? `$${employmentInfo.pensionBenefitAmount.toLocaleString()}` : ''
          });
        }
      }
    }
    
    // Partner Pensions
    if (personalInfo.hasPartner && employmentInfo.partnerHasPension !== undefined) {
      socialSecurityFields.push({
        name: 'Partner Has Pension',
        value: employmentInfo.partnerHasPension,
        isFilled: employmentInfo.partnerHasPension !== undefined,
        displayValue: employmentInfo.partnerHasPension ? 'Yes' : 'No'
      });
      
      if (employmentInfo.partnerHasPension || employmentInfo.partnerPensionStartAge || employmentInfo.partnerPensionBenefitAmount) {
        if (employmentInfo.partnerPensionStartAge !== undefined) {
          socialSecurityFields.push({
            name: 'Partner Pension Start Age',
            value: employmentInfo.partnerPensionStartAge,
            isFilled: isFieldFilled(employmentInfo.partnerPensionStartAge),
            displayValue: employmentInfo.partnerPensionStartAge ? `${employmentInfo.partnerPensionStartAge} years` : ''
          });
        }
        
        if (employmentInfo.partnerPensionBenefitAmount !== undefined) {
          socialSecurityFields.push({
            name: 'Partner Pension Benefit Amount',
            value: employmentInfo.partnerPensionBenefitAmount,
            isFilled: isFieldFilled(employmentInfo.partnerPensionBenefitAmount),
            displayValue: employmentInfo.partnerPensionBenefitAmount ? `$${employmentInfo.partnerPensionBenefitAmount.toLocaleString()}` : ''
          });
        }
      }
    }
    
    const socialSecurityFilledCount = socialSecurityFields.filter(f => f.isFilled).length;
    sections.push({
      name: 'Social Security & Pensions',
      fields: socialSecurityFields,
      completionPercentage: Math.round((socialSecurityFilledCount / socialSecurityFields.length) * 100)
    });

    // Savings & Investments Section
    const savingsFields: FieldStatus[] = [];
    const savingsInfo = formData?.savingsInfo || {};
    
    // The form stores balances as single values, not arrays
    // Tax-Deferred Assets (401k, IRA, etc.)
    if (!personalInfo.hasPartner) {
      // Single person - only client assets
      const taxDeferredBalance = Number(savingsInfo.taxDeferredAssets?.balance) || 0;
      savingsFields.push({
        name: 'Tax-Deferred Assets (401k, IRA, etc.)',
        value: taxDeferredBalance,
        isFilled: taxDeferredBalance > 0,
        displayValue: `$${taxDeferredBalance.toLocaleString()}`
      });
      
      const taxFreeBalance = Number(savingsInfo.taxFreeAssets?.balance) || 0;
      savingsFields.push({
        name: 'Tax-Free Assets (Roth, etc.)',
        value: taxFreeBalance,
        isFilled: taxFreeBalance > 0,
        displayValue: `$${taxFreeBalance.toLocaleString()}`
      });
      
      const taxableBalance = Number(savingsInfo.taxableAssets?.balance) || 0;
      savingsFields.push({
        name: 'Taxable Assets (Brokerage, etc.)',
        value: taxableBalance,
        isFilled: taxableBalance > 0,
        displayValue: `$${taxableBalance.toLocaleString()}`
      });
    } else {
      // Couple - show client and partner separately
      // Client's assets
      const client1TaxDeferred = Number(savingsInfo.taxDeferredAssets?.balance) || 0;
      savingsFields.push({
        name: `${personalInfo.client1FirstName}'s Tax-Deferred Assets`,
        value: client1TaxDeferred,
        isFilled: client1TaxDeferred > 0,
        displayValue: `$${client1TaxDeferred.toLocaleString()}`
      });
      
      const client1TaxFree = Number(savingsInfo.taxFreeAssets?.balance) || 0;
      savingsFields.push({
        name: `${personalInfo.client1FirstName}'s Tax-Free Assets`,
        value: client1TaxFree,
        isFilled: client1TaxFree > 0,
        displayValue: `$${client1TaxFree.toLocaleString()}`
      });
      
      // Partner's assets  
      if (savingsInfo.partnerInfo || savingsInfo.taxDeferredAssets?.partnerBalance !== undefined) {
        const client2TaxDeferred = Number(savingsInfo.taxDeferredAssets?.partnerBalance) || 0;
        savingsFields.push({
          name: `${personalInfo.client2FirstName}'s Tax-Deferred Assets`,
          value: client2TaxDeferred,
          isFilled: client2TaxDeferred > 0,
          displayValue: `$${client2TaxDeferred.toLocaleString()}`
        });
        
        const client2TaxFree = Number(savingsInfo.taxFreeAssets?.partnerBalance) || 0;
        savingsFields.push({
          name: `${personalInfo.client2FirstName}'s Tax-Free Assets`,
          value: client2TaxFree,
          isFilled: client2TaxFree > 0,
          displayValue: `$${client2TaxFree.toLocaleString()}`
        });
      }
      
      // Joint taxable assets
      const jointTaxable = Number(savingsInfo.taxableAssets?.jointBalance) || 0;
      savingsFields.push({
        name: 'Joint Taxable Assets',
        value: jointTaxable,
        isFilled: jointTaxable > 0,
        displayValue: `$${jointTaxable.toLocaleString()}`
      });
    }
    
    // Calculate and show total
    const client1Total = (Number(savingsInfo.taxDeferredAssets?.balance) || 0) + 
                        (Number(savingsInfo.taxFreeAssets?.balance) || 0) + 
                        (personalInfo.hasPartner ? 0 : (Number(savingsInfo.taxableAssets?.balance) || 0));
    
    const partnerTotal = personalInfo.hasPartner ? 
                        (Number(savingsInfo.taxDeferredAssets?.partnerBalance) || 0) + 
                        (Number(savingsInfo.taxFreeAssets?.partnerBalance) || 0) : 0;
    
    const jointTotal = personalInfo.hasPartner ? (Number(savingsInfo.taxableAssets?.jointBalance) || 0) : 0;
    
    const grandTotal = client1Total + partnerTotal + jointTotal;
    
    savingsFields.push({
      name: 'Total Assets',
      value: grandTotal,
      isFilled: grandTotal > 0,
      displayValue: `$${grandTotal.toLocaleString()}`
    });
    
    const savingsFilledCount = savingsFields.filter(f => f.isFilled).length;
    sections.push({
      name: 'Savings & Investments',
      fields: savingsFields,
      completionPercentage: Math.round((savingsFilledCount / savingsFields.length) * 100)
    });

    // Goals & Priorities Section
    const goalsFields: FieldStatus[] = [];
    const goalsInfo = formData?.goalsInfo || {};
    
    goalsFields.push({
      name: 'Target Income',
      value: goalsInfo.targetIncome,
      isFilled: isFieldFilled(goalsInfo.targetIncome),
      displayValue: goalsInfo.targetIncome ? `$${goalsInfo.targetIncome.toLocaleString()}` : ''
    });
    
    const selectedGoals = goalsInfo.selectedGoals || {};
    const goalsList = [];
    if (selectedGoals.financialIndependence) goalsList.push('Financial Independence');
    if (selectedGoals.travel) goalsList.push('Travel');
    if (selectedGoals.legacy) goalsList.push('Legacy');
    if (selectedGoals.healthcare) goalsList.push('Healthcare');
    if (selectedGoals.lifestyle) goalsList.push('Lifestyle');
    if (selectedGoals.other) goalsList.push('Other');
    
    goalsFields.push({
      name: 'Selected Goals',
      value: goalsList,
      isFilled: goalsList.length > 0,
      displayValue: goalsList.join(', ') || 'None selected'
    });
    
    if (selectedGoals.other && goalsInfo.otherGoalDescription) {
      goalsFields.push({
        name: 'Other Goal Description',
        value: goalsInfo.otherGoalDescription,
        isFilled: isFieldFilled(goalsInfo.otherGoalDescription),
        displayValue: goalsInfo.otherGoalDescription || ''
      });
    }
    
    const goalsFilledCount = goalsFields.filter(f => f.isFilled).length;
    sections.push({
      name: 'Goals & Priorities',
      fields: goalsFields,
      completionPercentage: Math.round((goalsFilledCount / goalsFields.length) * 100)
    });

    return sections;
  };

  // Load incomplete forms from MongoDB or localStorage
  const loadForms = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const incompleteForms = await progressStorageService.getIncompleteSessions();
      const formattedForms = incompleteForms.map(session => ({
        sessionId: session.sessionId,
        customerId: session.customerId,
        firstName: session.firstName,
        lastName: session.lastName,
        email: session.email,
        phone: session.phone,
        currentStep: session.currentStep,
        stepName: session.stepName,
        lastUpdated: session.lastUpdated,
        formData: session.formData
      }));
      
      setForms(formattedForms);
      setFilteredForms(formattedForms);
    } catch (err) {
      setError('Failed to load incomplete forms');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadForms();
  }, []);

  // Bulk-fetch advisor names for all unique customer IDs
  useEffect(() => {
    const fetchAdvisorNames = async () => {
      const uniqueIds = Array.from(new Set(forms.map(f => f.customerId).filter(Boolean))) as string[];
      const names = new Map<string, string>();
      await Promise.all(
        uniqueIds.map(async (id) => {
          try {
            const config = await fetchAdvisorConfig(id);
            if (config?.name) names.set(id, config.name);
          } catch { /* ignore */ }
        })
      );
      setAdvisorNames(names);
    };
    if (forms.length > 0) fetchAdvisorNames();
  }, [forms]);

  // Filter forms based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredForms(forms);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = forms.filter(form => 
        form.firstName?.toLowerCase().includes(term) ||
        form.lastName?.toLowerCase().includes(term) ||
        form.email?.toLowerCase().includes(term) ||
        form.phone?.includes(term) ||
        form.customerId?.includes(term)
      );
      setFilteredForms(filtered);
    }
  }, [searchTerm, forms]);

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Get advisor name for a customer ID
  const getAdvisorName = (customerId: string) => {
    return advisorNames.get(customerId) || `Unknown Advisor`;
  };

  // Calculate completion percentage
  const getCompletionPercentage = (step: number, isComplete?: boolean) => {
    // If the form is marked as complete, show 100%
    if (isComplete) {
      return 100;
    }
    // Step 6 is the Review step, but we only want to show 100% after submission
    // So max out at 90% for the Review step
    if (step >= 6) {
      return 90;  // Review step shows as 90%, only becomes 100% after submission
    }
    return Math.round((step / 6) * 90);  // Scale other steps to max of 90%
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Session ID', 'First Name', 'Last Name', 'Email', 'Phone', 'Advisor Name', 'Customer ID', 'Current Step', 'Completion %', 'Last Updated'];
    const rows = filteredForms.map(form => [
      form.sessionId,
      form.firstName || '',
      form.lastName || '',
      form.email || '',
      form.phone || '',
      getAdvisorName(form.customerId),
      form.customerId,
      form.stepName,
      `${getCompletionPercentage(form.currentStep, form.formData?.completed)}%`,
      formatDate(form.lastUpdated)
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `incomplete-forms-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Generate resume link
  const generateResumeLink = (form: IncompleteForm) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/wizard/${form.customerId}?session=${form.sessionId}`;
  };

  // View form details
  const handleViewDetails = (form: IncompleteForm) => {
    setSelectedForm(form);
    setViewDialogOpen(true);
  };

  // Delete a form
  const handleDelete = async (sessionId: string) => {
    if (window.confirm('Are you sure you want to delete this form? This cannot be undone.')) {
      try {
        await progressStorageService.deleteSession(sessionId);
        // Reload forms after deletion
        await loadForms();
      } catch (error) {
        console.error('Failed to delete form:', error);
        setError('Failed to delete form');
      }
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuthenticated');
    sessionStorage.removeItem('adminLoginTime');
    navigate('/admin');
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading incomplete forms...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1">
            Incomplete Forms
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              onClick={() => navigate('/admin/completed-forms')}
              variant="outlined"
            >
              View Completed Forms
            </Button>
            <Button
              startIcon={<RefreshIcon />}
              onClick={loadForms}
              variant="outlined"
            >
              Refresh
            </Button>
            <Button
              startIcon={<DownloadIcon />}
              onClick={exportToCSV}
              variant="contained"
              disabled={filteredForms.length === 0}
            >
              Export CSV
            </Button>
            <Button
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              variant="outlined"
              color="error"
            >
              Logout
            </Button>
          </Stack>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by name, email, phone, or customer ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {filteredForms.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="textSecondary">
              {searchTerm ? 'No forms match your search' : 'No incomplete forms found'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Advisor Name</TableCell>
                  <TableCell>Customer ID</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredForms.map((form) => (
                  <TableRow key={form.sessionId}>
                    <TableCell>
                      {form.firstName || form.lastName ? (
                        `${form.firstName || ''} ${form.lastName || ''}`.trim()
                      ) : (
                        <em>Not provided</em>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box>
                        {form.email && (
                          <Typography variant="body2">{form.email}</Typography>
                        )}
                        {form.phone && (
                          <Typography variant="body2" color="textSecondary">
                            {form.phone}
                          </Typography>
                        )}
                        {!form.email && !form.phone && (
                          <em>Not provided</em>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{getAdvisorName(form.customerId)}</TableCell>
                    <TableCell>{form.customerId}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={`${getCompletionPercentage(form.currentStep, form.formData?.completed)}%`}
                          size="small"
                          color={form.currentStep >= 5 ? 'success' : form.currentStep >= 3 ? 'warning' : 'default'}
                        />
                        <Typography variant="caption" color="textSecondary">
                          {form.stepName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(form.lastUpdated)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(form)}
                        title="View details"
                      >
                        <ViewIcon />
                      </IconButton>
                      {form.email && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            const link = generateResumeLink(form);
                            const subject = encodeURIComponent('Complete Your Retirement Assessment');
                            const body = encodeURIComponent(`Hi ${form.firstName || 'there'},\n\nYou can complete your retirement assessment by clicking this link:\n${link}\n\nBest regards`);
                            window.location.href = `mailto:${form.email}?subject=${subject}&body=${body}`;
                          }}
                          title="Send resume email"
                        >
                          <EmailIcon />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(form.sessionId)}
                        title="Delete form"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Form Details</Typography>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
            >
              <ToggleButton value="fields">
                <ViewListIcon sx={{ mr: 1 }} />
                Field Overview
              </ToggleButton>
              <ToggleButton value="json">
                <CodeIcon sx={{ mr: 1 }} />
                Raw JSON
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedForm && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Session Information</Typography>
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2"><strong>Session ID:</strong> {selectedForm.sessionId}</Typography>
                <Typography variant="body2"><strong>Customer ID:</strong> {selectedForm.customerId}</Typography>
                <Typography variant="body2"><strong>Last Updated:</strong> {formatDate(selectedForm.lastUpdated)}</Typography>
                <Typography variant="body2"><strong>Current Step:</strong> {selectedForm.stepName} (Step {selectedForm.currentStep + 1})</Typography>
                <Typography variant="body2"><strong>Completion:</strong> {getCompletionPercentage(selectedForm.currentStep, selectedForm.formData?.completed)}%</Typography>
              </Box>

              <Typography variant="subtitle2" gutterBottom>Resume Link</Typography>
              <TextField
                fullWidth
                value={generateResumeLink(selectedForm)}
                InputProps={{ readOnly: true }}
                variant="outlined"
                size="small"
                sx={{ mb: 3 }}
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />

              {viewMode === 'fields' ? (
                <>
                  <Typography variant="subtitle2" gutterBottom>Form Data Overview</Typography>
                  <Grid container spacing={2}>
                    {analyzeFormData(selectedForm.formData).map((section, index) => (
                      <Grid item xs={12} key={index}>
                        <Card variant="outlined">
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="h6" component="div">
                                {section.name}
                              </Typography>
                              <Chip
                                label={`${section.completionPercentage}% Complete`}
                                color={section.completionPercentage === 100 ? 'success' : section.completionPercentage >= 50 ? 'warning' : 'default'}
                                size="small"
                              />
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            <List dense>
                              {section.fields.map((field, fieldIndex) => (
                                <ListItem key={fieldIndex}>
                                  <ListItemIcon sx={{ minWidth: 36 }}>
                                    {field.isFilled ? (
                                      <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                                    ) : (
                                      <CancelIcon sx={{ color: 'error.main', fontSize: 20 }} />
                                    )}
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography 
                                          variant="body2" 
                                          sx={{ 
                                            color: field.isFilled ? 'text.primary' : 'text.secondary',
                                            fontWeight: field.isFilled ? 500 : 400
                                          }}
                                        >
                                          {field.name}
                                        </Typography>
                                        <Typography 
                                          variant="body2" 
                                          sx={{ 
                                            color: field.isFilled ? 'success.dark' : 'error.dark',
                                            ml: 2
                                          }}
                                        >
                                          {field.isFilled ? field.displayValue : 'Not provided'}
                                        </Typography>
                                      </Box>
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </>
              ) : (
                <>
                  <Typography variant="subtitle2" gutterBottom>Form Data (JSON)</Typography>
                  <Box
                    component="pre"
                    sx={{
                      p: 2,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: 400,
                      fontSize: '0.875rem'
                    }}
                  >
                    {JSON.stringify(selectedForm.formData, null, 2)}
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IncompleteFormsView;